import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './MultiSearchableSelect.module.css';

export interface SearchableOption {
  id: string;
  label: string;
  subtitle?: string;
}

interface Props {
  options: SearchableOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function MultiSearchableSelect({
  options,
  selectedValues,
  onChange,
  placeholder = 'Search and select states...',
  disabled = false,
}: Props) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(
    (opt) =>
      opt.label.toLowerCase().includes(query.toLowerCase()) ||
      (opt.subtitle && opt.subtitle.toLowerCase().includes(query.toLowerCase())),
  );

  const toggleOption = (id: string) => {
    if (selectedValues.includes(id)) {
      onChange(selectedValues.filter((v) => v !== id));
    } else {
      onChange([...selectedValues, id]);
    }
  };

  const removeValue = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedValues.filter((v) => v !== id));
  };

  const handleSelectAll = () => {
    if (selectedValues.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map((o) => o.id));
    }
  };

  const selectedOptions = options.filter((o) => selectedValues.includes(o.id));

  return (
    <div className={styles.container} ref={containerRef}>
      <div
        className={`${styles.selectBox} ${isOpen ? styles.open : ''} ${disabled ? styles.disabled : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className={styles.pillsContainer}>
          {selectedOptions.length === 0 ? (
            <span className={styles.placeholder}>{placeholder}</span>
          ) : (
            selectedOptions.map((opt) => (
              <span key={opt.id} className={styles.pill}>
                {opt.label}
                <button
                  type="button"
                  className={styles.removeBtn}
                  onClick={(e) => removeValue(opt.id, e)}
                  title={`Remove ${opt.label}`}
                >
                  ×
                </button>
              </span>
            ))
          )}
        </div>
        <span className={styles.arrow}>{isOpen ? '▲' : '▼'}</span>
      </div>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.searchHeader}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder={t('ui.multiSearchableSelect.filterPlaceholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
            <button
              type="button"
              className={styles.selectAllBtn}
              onClick={(e) => {
                e.stopPropagation();
                handleSelectAll();
              }}
            >
              {selectedValues.length === options.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          <div className={styles.optionsList}>
            {filteredOptions.length === 0 ? (
              <div className={styles.noMatch}>{t('ui.multiSearchableSelect.noMatch')}</div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = selectedValues.includes(opt.id);
                return (
                  <div
                    key={opt.id}
                    className={`${styles.optionItem} ${isSelected ? styles.selected : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleOption(opt.id);
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      readOnly
                      className={styles.checkbox}
                    />
                    <div className={styles.optionText}>
                      <span className={styles.optionLabel}>{opt.label}</span>
                      {opt.subtitle && (
                        <span className={styles.optionSubtitle}>{opt.subtitle}</span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
